import {
  Box,
  Button,
  ButtonProps,
  forwardRef,
  Icon,
  ResponsiveValue,
  SystemStyleObject,
  Text,
  useBoolean,
} from '@chakra-ui/react'
import React from 'react'
import {
  MangoButtonVariant,
  useMangoButtonStyleConfig,
} from '~/components/shared/componentStyles/MangoButton.style'
import { Size } from '~/types/ThemeTypes'

export interface MangoButtonProps
  extends Omit<
    ButtonProps,
    'children' | 'size' | 'onClick' | 'variant' | 'leftIcon' | 'rightIcon'
  > {
  disabled?: boolean
  leftIcon?: SVGComponent
  leftIconProps?: SystemStyleObject
  rightIcon?: SVGComponent
  rightIconProps?: SystemStyleObject
  leftNode?: React.ReactNode
  rightNode?: React.ReactNode
  size?: ResponsiveValue<Size.md | Size.sm>
  variant?: MangoButtonVariant
  children?: string | React.ReactNode
  onClick?: () => void
  onHoverStateChange?: (isHovered: boolean) => void
}
/**
 * The standard button used throughout the application.
 * @param text The text to display on the button.
 * @param disabled Whether the button is disabled. Defaults to false.
 * @param size The size of the button. Defaults to Medium.
 * @param variant The variant of the button. Defaults to Primary.
 * @param leftIcon The icon to display on the left side of the button.
 * @param rightIcon The icon to display on the right side of the button.
 * @param leftContent The content to display on the left side of the button. Overrides leftIcon.
 * @param rightContent The content to display on the right side of the button. Overrides rightIcon.
 * @param onClick The function to call when the button is clicked.
 * @returns
 */
export const MangoButton = forwardRef<MangoButtonProps, 'button'>(
  (
    {
      disabled = false,
      leftIcon,
      leftIconProps,
      rightIcon,
      rightIconProps,
      leftNode,
      rightNode,
      size = Size.md,
      variant = MangoButtonVariant.Default_300,
      children,
      onClick,
      onHoverStateChange,
      ...rest
    }: MangoButtonProps,
    ref,
  ) => {
    const [_, setHoverState] = useBoolean(false)
    const handleMouseEnter = () => {
      setHoverState.on()
      onHoverStateChange?.(true)
    }
    const handleMouseLeave = () => {
      setHoverState.off()
      onHoverStateChange?.(false)
    }

    const styles = useMangoButtonStyleConfig({
      variant,
      size,
    })

    const handleClick = () => {
      if (disabled) return
      onClick?.()
    }

    const getLeftContent = () => {
      if (!leftIcon && !leftNode) {
        return null
      }
      const content = leftNode ?? (
        <Icon as={leftIcon} sx={{ ...styles.icon, ...leftIconProps }} />
      )
      return <Box sx={styles.nodeContainer}>{content}</Box>
    }

    const getRightContent = () => {
      if (!rightIcon && !rightNode) {
        return null
      }

      const content = rightNode ?? (
        <Icon as={rightIcon} sx={{ ...styles.icon, ...rightIconProps }} />
      )
      return <Box sx={styles.nodeContainer}>{content}</Box>
    }

    return (
      <Box
        data-group
        as={Button}
        sx={{ ...styles.button }}
        onClick={handleClick}
        aria-disabled={disabled}
        ref={ref}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {getLeftContent()}
        <Text sx={styles.text}>{children}</Text>
        {getRightContent()}
      </Box>
    )
  },
)
